/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderUserContext,
  CommonActions,
  communicationAttachmentToFile,
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
  FormulaCalculationService,
  FormulaTestEvent,
  getComponentItemFinderListItemId,
  getSalesTaxRateListItemId,
  GraphicalTakeOffMainConfig,
  graphicalTakeOffMainDialogRoute,
  isFormulaVariableActive,
  SalesTaxRateFinderDialogConfig,
  salesTaxRateFinderDialogRoute,
  variablesToSnippets,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ComponentItemFilterMode,
  FormulaElementGroup,
  JeVariationStatus,
} from '@gh/builder-shared-data';
import { FileListItem } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  ContainerState,
  getAllNewSelectedEntities,
  getAllSelectedEntities,
  getRouteInitData,
  getSingleNewSelectedEntity,
  newSelectionModel,
  NumberTypes,
  PAGE_STATE,
  queryDirty,
  queryVersion,
  setEntityField,
  State,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { memoryBasedSlicedDataSource } from '@gh/core-data';
import { MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  WaitingContext,
  WaitingMode,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { openInNewWindow, toString } from '@gh/core-util';
import { combineLatest$, concat$, EMPTY$, of$, throwError$ } from '@gh/rx';
import { filter$, map$, mapTo$, switchMap$, tap$ } from '@gh/rx/operators';
import { flatMap, flatten, isEmpty, isNil, keyBy, mapValues, uniq, values } from 'lodash';
import { Observable } from 'rxjs';
import { jeAddOrdersDialogRoute, JobOrderDetailsDialogConfig, jobOrderDetailsDialogRoute } from '../../';
import { JobEstimateDetailsBusinessService } from '../../resources/job-estimate-details-business.service';
import {
  getJeComponentId,
  getJeCostCenterId,
  getJeItemId,
  getJeTopId,
  isJeCcNotPreloaded,
  isJeComponent,
  isJeItem,
  isJeRecordForComponent,
  JeAddOrders,
  JeCcFilter,
  JeComponent,
  JeCostCenter,
  jeCostCenterFromFinder,
  jeCostCenterItemsFromFinder,
  jeCostCenterToFinderListItem,
  JeDetails,
  JeDetailsProcess,
  JeElement,
  JeElementType,
  jeFormulaVariableFromTop,
  JeGetComponentsResponse,
  JeGetCostCenterResponse,
  JeItem,
  JeMappingContext,
  JeOrderForSchedule,
  jeOrderFromGenerated,
  jeOrderFromOrderDetails,
  jeOrderToOrderDetails,
  jeStrFromFinder,
  jeStrToFinder,
  JeTop,
  JeTopViewUsageRecord,
  newJeCuttingListItem,
} from '../../resources/job-estimate-details.model';
import { jeOrderDetailsDialogRoute } from './je-cc-order-details-dialog/je-cc-order-details-dialog.component';
import {
  JeDetailsState,
  JeDetailsStateCostCenterLoadEvent,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  queryJeAllowSupplierChange,
  queryJeAuthorized,
  queryJeComponent,
  queryJeCostCenter,
  queryJeCostCenterByCode,
  queryJeCostCenterHasDirtyChanges,
  queryJeCostCenterOrder0,
  queryJeCostCenterOrderByNumber,
  queryJeCostCenters,
  queryJeCostCentersByName,
  queryJeDirtyData,
  queryJeEditable,
  queryJeElement,
  queryJeElementFormulaCalculation,
  queryJeElementHasErrors,
  queryJeFormulaFirstInvalidTop,
  queryJeHasCostCenter,
  queryJeHasErrors,
  queryJeHasTopsFromGto,
  queryJeIsCccId,
  queryJeIsCccSelected,
  queryJeItem,
  queryJeJobId,
  queryJeMasterInfo,
  queryJeNextOrderNumber,
  queryJeNextUnconfirmedVariation,
  queryJeOrder,
  queryJeOrderDetailsMasterInfo,
  queryJeRedoDisabled,
  queryJeSalesTaxRates,
  queryJeSelectedCostCenterId,
  queryJeTop,
  queryJeTopRefItem,
  queryJeTopRefItems,
  queryJeTops,
  queryJeTopViewUsage,
  queryJeUndoDisabled,
  queryJeUomRefItem,
} from './je-details.state';
import { jeTopViewUsageDialogRoute } from './je-top-usage-dialog/je-top-usage-dialog.component';
import { WaitingProcess } from '../../../../core-ui/waiting/waiting-process';

@Injectable()
export class JeCommandService {
  isUndoDisabled$ = this.state.select(queryJeUndoDisabled);
  isRedoDisabled$ = this.state.select(queryJeRedoDisabled);
  memoDebounce: ReturnType<typeof setTimeout>;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private context: BuilderUserContext,
              private translations: TranslationService,
              @Inject(PAGE_STATE) private form: ContainerState<JeDetailsState>,
              private state: State<JeDetailsState>,
              private business: JobEstimateDetailsBusinessService,
              private waitingService: WaitingService,
              private waitingContext: WaitingContext,
              private dialogService: DialogService,
              private notificationService: NotificationService,
              private formulaCalculation: FormulaCalculationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions) {

  }

  initHistory(): void  {
    this.state.mutate({ type: JeDetailsStateEventType.InitHistory});
  }

  saveViewToHistory(): void  {
    this.state.mutate({ type: JeDetailsStateEventType.SaveViewToHistory});
  }

  addToHistory(): void  {
    this.state.mutate({ type: JeDetailsStateEventType.AddToHistory});
  }

  undo(): void  {
    this.state.mutate({ type: JeDetailsStateEventType.Undo});
  }

  redo(): void {
    this.state.mutate({ type: JeDetailsStateEventType.Redo});
  }

  load(details: JeDetails, layouts: Hash<GridLayout>, hasEditPermission: boolean): void {
    this.state.mutate({ type: JeDetailsStateEventType.Load, details, layouts, hasEditPermission });
  }

  costCenterAdd(): void {
    this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: false,
      preselected: this.state.project(queryJeCostCenters).map(getJeCostCenterId),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity),
      map$(jeCostCenterFromFinder),
      // create default order for new cost center
      switchMap$((costCenter: JeCostCenter) => this.business.generateOrders([getJeCostCenterId(costCenter)]).pipe(
        (this.waitingService.operator()),
        map$(([order]) => ({
          order: jeOrderFromGenerated(order, 0),
          costCenter,
        })))))
      .subscribe(({ costCenter, order }) => {
        this.state.mutate({
          type: JeDetailsStateEventType.CostCenterAdd,
          costCenter,
          order,
        });
        this.addToHistory();
      },
    );
  }

  costCenterViewOrderDetails(costCenter: JeCostCenter): void {
    const masterInfo = this.state.project(queryJeMasterInfo);
    this.dialogService.openWithInput(jeOrderDetailsDialogRoute, {
      jobNumber: masterInfo.jobNumber,
      siteAddress: masterInfo.siteAddress,
      jobId: this.state.project(queryJeJobId),
      costCenterId: getJeCostCenterId(costCenter),
      dirtyData: this.state.project(queryJeDirtyData),
    });
  }

  costCenterSetField(costCenterId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: JeDetailsStateEventType.CostCenterSetField, costCenterId, name, value });
      this.addToHistory();
    };
    if (this.memoDebounce) {
      clearTimeout(this.memoDebounce);
    }
    if (name === 'memo') {
      // @ts-ignore
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else {
      doAction();
    }
  }

  costCenterSelect(costCenterId?: ID): void {
    if (!this.validate()) {
      return;
    }
    concat$(
      this.ensureCcLoad(costCenterId),
      of$({ type: JeDetailsStateEventType.CostCenterSelect, costCenterId }),
    ).subscribe(
      (event) => this.state.mutate(event),
      this.notificationService.processAll);
  }

  setCostCenterFilter(filter: JeCcFilter): void {
    this.state.mutate({ type: JeDetailsStateEventType.CostCenterSetFilter, filter });
  }

  topViewInfo(top: JeTop): void {
    const viewUsage = this.state.project(queryJeTopViewUsage, getJeTopId(top));
    this.dialogService.openWithInput(jeTopViewUsageDialogRoute, viewUsage)
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((record: JeTopViewUsageRecord) => {
        const currentCcId = this.state.project(queryJeSelectedCostCenterId);

        if (isJeRecordForComponent(record)) {
          if (!this.state.project(queryJeIsCccId, currentCcId) && !this.validate()) {
            return;
          }

          this.componentSelect(record.recordId);
        } else {
          const item = this.state.project(queryJeItem, record.recordId);
          if (item && item.costCenterId !== currentCcId && !this.validate()) {
            return;
          }
          this.itemSelect(record.recordId);
        }
      });
  }

  topRemove(top: JeTop): void {
    this.state.mutate({ type: JeDetailsStateEventType.TopRemove, topId: top.topId });
  }

  topSetField(topId: ID, name: string, value: any): void {
    const top = this.state.project(queryJeTop, topId);

    if (top && top[name] !== value) {
      // set top value temporarily
      this.state.mutate({ type: JeDetailsStateEventType.TopSetField, topId, name, value });

      if (top.isFromGto) {
        this.dialogService.confirmation(yesNoConfirmation('msg_top_gto_remove'))
          .subscribe((answer) => {
            // commit change if user has confirmed change, otherwise rollback original value
            if (yesAnswer(answer)) {
              this.state.mutate({ type: JeDetailsStateEventType.TopCommit, topId });
            } else {
              this.state.mutate({ type: JeDetailsStateEventType.TopReset, top });
            }
          });
      } else {
        this.state.mutate({ type: JeDetailsStateEventType.TopCommit, topId });
      }

      this.addToHistory();
    }
  }

  componentAdd(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.Components,
      disableFilterMode: true,
      multiselection: true,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: ComponentItemFinderListItem[]) => this.ensureDependenciesExist(items).pipe(mapTo$(items))),
      switchMap$((components: ComponentItemFinderListItem[]) =>
        this.business.getComponents(
          this.state.project(queryJeJobId),
          components.map(getComponentItemFinderListItemId),
          this.createJeMappingContext()).pipe(
          switchMap$(this.generateOrdersForNewCostCenters.bind(this)),
          this.waitingService.operator<JeGetComponentsResponse>())),
      map$((componentSet) => this.mergeFirstOrdersIntoComponentItems(componentSet)))
      .subscribe((componentSet) => {
        this.state.mutate({ type: JeDetailsStateEventType.ComponentAddSet, componentSet });
        this.addToHistory();
      },
    );
  }

  componentClone(component: JeComponent): void {
    const componentToClone = component.componentId;
    if (componentToClone) {
      this.business.getComponents(
        this.state.project(queryJeJobId),
        [{ id: componentToClone, count: 1 }], 
        this.createJeMappingContext()
      ).pipe(
        switchMap$(this.generateOrdersForNewCostCenters.bind(this)),
        map$((componentSet) => ({
          ...componentSet,
          components: componentSet.components.map((c: JeComponent) => ({
            ...c,
            quantity: component.quantity,
            extension: component.extension,
            description: component.description,
            formula: component.formula,
            memo: component.memo,
            cuttingList: component.cuttingList,
            
          }))
        })),
        this.waitingService.operator<JeGetComponentsResponse>(),
        map$((componentSet) => this.mergeFirstOrdersIntoComponentItems(componentSet))
      ).subscribe((componentSet) => {
        this.state.mutate({ 
          type: JeDetailsStateEventType.ComponentAddSet, 
          componentSet 
        });
        this.addToHistory();
      });
    }
  }

  componentSetField(componentId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: JeDetailsStateEventType.ComponentSetField, componentId, name, value });
      this.addToHistory();
    };
    if (this.memoDebounce) { clearTimeout(this.memoDebounce); }
    if (name === 'memo') {
      // tslint:disable-next-line:no-magic-numbers
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else { doAction(); }
  }

  componentRemove(component: JeComponent): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_he_component_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
          this.state.mutate({
          type: JeDetailsStateEventType.ComponentRemove,
          componentId: getJeComponentId(component),
        });
          this.addToHistory();
      });
  }

  componentSelect(componentId?: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concat$(
      of$({ type: JeDetailsStateEventType.ComponentSelect, componentId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  cuttingListItemAdd(sourceType: JeElementType, sourceId: ID): void {
    const parent = this.state.project(queryJeElement, sourceType, sourceId);

    this.state.mutate({
      type: JeDetailsStateEventType.CuttingListItemAdd,
      sourceType,
      sourceId,
      item: newJeCuttingListItem(parent.uomId, -1, sourceType, sourceId, this.createJeMappingContext()),
    });

    this.addToHistory();
  }

  cuttingListItemSetField(sourceType: JeElementType,
                          sourceId: ID,
                          cuttingId: ID,
                          name: string,
                          value: any): void {
    this.state.mutate({
      type: JeDetailsStateEventType.CuttingListItemSetField,
      sourceType,
      sourceId,
      cuttingId,
      name,
      value,
    });
    this.addToHistory();
  }

  cuttingListItemRemove(sourceType: JeElementType, sourceId: ID, cuttingId: ID): void {
    this.state.mutate({
      type: JeDetailsStateEventType.CuttingListItemRemove,
      sourceType,
      sourceId,
      cuttingId,
    });
    this.addToHistory();
  }

  costCenterItemAdd(costCenterId: ID): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.Items,
      disableFilterMode: true,
      multiselection: true,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: ComponentItemFinderListItem[]) => this.ensureDependenciesExist(items).pipe(mapTo$(items))),
      switchMap$((items: ComponentItemFinderListItem[]) => {
        const duplicatedItems: ComponentItemFinderListItem[] = items.reduce((acc, item) => {
          const count = item.count || 1;
          const copies = Array(count).fill(null).map(() => ({ ...item }));
          return acc.concat(copies);
        }, [] as ComponentItemFinderListItem[]);
        
        return this.business.generateItemIds(duplicatedItems.length).pipe(
          map$((ids) => jeCostCenterItemsFromFinder(
            costCenterId,
            ids,
            duplicatedItems,
            this.createJeMappingContext())),
          (this.waitingService.operator()))
      }),
      map$((items) => this.mergeFirstOrdersIntoItems(items)))
      .subscribe((items: JeItem[]) => {
        this.saveViewToHistory();
        this.state.mutate({
          type: JeDetailsStateEventType.CostCenterItemAddSet,
          costCenterId,
          itemSet: items,
        });
        this.addToHistory();
      });
  }

  costCenterItemClone(item: JeItem): void {
    if (item) {
      this.business.generateItemIds(1).pipe(
        map$((ids) => ([{
          ...item,
          jobItemId: ids[0],
          quantity: item.quantity,
          extension: item.extension,
          formula: item.formula,
          memo: item.memo,
          cuttingList: item.cuttingList,
          createdDate: new Date()
        }])),
        map$((items) => this.mergeFirstOrdersIntoItems(items)),
        this.waitingService.operator()
      ).subscribe((items: JeItem[]) => {
        this.state.mutate({
          type: JeDetailsStateEventType.CostCenterItemAddSet,
          costCenterId: item.costCenterId,
          itemSet: items,
        });
        this.addToHistory();
      });
    }
  }

  costCenterItemSetField(itemId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: JeDetailsStateEventType.CostCenterItemSetField, itemId, name, value });
      this.addToHistory();
    };
    if (this.memoDebounce) { clearTimeout(this.memoDebounce); }
    if (name === 'memo') {
      // tslint:disable-next-line:no-magic-numbers
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else { doAction(); }
  }

  costCenterItemRemove(item: JeItem): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_he_item_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.state.mutate({
          type: JeDetailsStateEventType.CostCenterItemRemove,
          itemId: getJeItemId(item),
        });
        this.addToHistory();
      },
    );
  }

  itemSelect(itemId?: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concat$(
      of$(itemId).pipe(
        filter$(Boolean),
        map$((id) => this.state.project(queryJeItem, id)),
        filter$(Boolean),
        switchMap$((item: any) => this.ensureCcLoad(item.costCenterId))),
      of$({ type: JeDetailsStateEventType.CostCenterItemSelect, itemId }),
    ).subscribe(
      (event) => this.state.mutate(event),
      this.notificationService.processAll);
  }

  /**
   * We have separate method for cost center item selection accepting additional costCenter parameter.
   * This is necessary at least for component item selection, because target item is not stored in the store,
   * thus it is impossible to determine target cost center.
   *
   * @param {ID} costCenterId
   * @param {ID} itemId
   */
  costCenterItemSelect(costCenterId: ID, itemId: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concat$(
      this.ensureCcLoad(costCenterId),
      of$({ type: JeDetailsStateEventType.CostCenterItemSelect, itemId }),
    ).subscribe(
      (event) => this.state.mutate(event),
      this.notificationService.processAll);
  }

  tabSelect(name: string): void {
    if (!this.validateCurrentElement()) {
      return;
    }
    this.state.mutate({ type: JeDetailsStateEventType.DetailsTabSelect, name });
  }

  autoSave(): Observable<any> {
    if (!this.state.project(queryDirty)) {
      return EMPTY$;
    }

    return this.dialogService.confirmation(yesNoConfirmation('msg_unsaved_info')).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const flow = this.completeFlow();

        return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form }).toFinalState();
      }));
  }

  recalculate(): void {
    const flow = this.recalculateFlow();

    flow.run({
      form: this.form,
      value: this.form.submitValue,
    });
  }

  completeFlow(): ActionFlow<FormFlowState<any>> {
    const { state } = this;
    let process: WaitingProcess;
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryJeHasErrors),
        () => this.handleFormError()),
      execute: this.commonActions.execute((fs: FormFlowState<any>) => {
        process = this.waitingContext.start(WaitingMode.Blocked);
        return this.business.process(
          state.project(queryJeJobId),
          state.project(queryJeDirtyData),
          state.project(queryJeIsCccSelected) ? void 0 : state.project(queryJeSelectedCostCenterId),
          this.createJeMappingContext(),
        ).pipe(switchMap$((res: JeDetailsProcess) => {
          if (process) {
            process.stop();
          }
          if (res.jobOrdersForSchedule && res.jobOrdersForSchedule.length) {
            return this.openAddOrdersDialog(res.jobOrdersForSchedule, res);
          }
          return of$(res);
        }));
      },
                                          true,
                                          (error: any) => {
                                            if (process) {
                                              process.stop();
                                            }
                                            this.notificationService.processAll(error);
      },
      ),
      finish: (fs: FormFlowState<any>) => {
        this.state.mutate({
          type: JeDetailsStateEventType.LoadWithCostCenter,
          details: fs.value,
          costCenterId: fs.value.initialCostCenterId,
          costCenter: fs.value.initialCostCenter,
        });

        this.updateFormulaContext();
        this.initHistory();
      },
    });
  }

  recalculateFlow(): ActionFlow<FormFlowState<JeDetailsState>> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryJeHasErrors),
        () => this.handleFormError()),
      execute: this.commonActions.execute((state: FormFlowState<JeDetailsState>) =>
        <any>this.business.recalculate(
          this.state.project(queryJeJobId),
          this.state.project(queryJeDirtyData),
          this.createJeMappingContext())),
      finish: (state: FormFlowState<JeDetailsState>) => {
        this.state.mutate({
          type: JeDetailsStateEventType.MergeOnRecalculate,
          details: state.value,
        });

        this.updateFormulaContext();
      },
    });
  }

  repriceFlow(): ActionFlow<FormFlowState<JeDetailsState>> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryJeHasErrors),
        () => this.handleFormError()),
      execute: this.commonActions.execute((state: FormFlowState<JeDetailsState>) =>
        <any>this.business.reprice(
          this.state.project(queryJeJobId),
          this.state.project(queryJeDirtyData),
          this.createJeMappingContext())),
      finish: (state: FormFlowState<JeDetailsState>) => this.state.mutate({
        type: JeDetailsStateEventType.MergeOnReprice,
        details: state.value,
      }),
    });
  }

  handleFormError(): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    this.state.mutate({ type: JeDetailsStateEventType.NavigateToInvalid });

    this.notificationService.error('msg_he_incorrect_data_in_tables');
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: JeDetailsStateEventType.ResetFocusedCell });
  }

  testItemFormula(event: FormulaTestEvent, itemId: ID): void {
    const item = this.state.project(queryJeItem, itemId);
    if (item) {
      this.testElementFormula(event, item);
    }
  }

  testComponentFormula(event: FormulaTestEvent, componentId: ID): void {
    const component = this.state.project(queryJeComponent, componentId);
    if (component) {
      this.testElementFormula(event, component);
    }
  }

  navigateToNextUnconfirmedVariation(): void {
    const element = this.state.project(queryJeNextUnconfirmedVariation);
    if (element && isJeComponent(element)) {
      this.componentSelect(getJeComponentId(element));
    } else if (element && isJeItem(element)) {
      this.itemSelect(getJeItemId(element));
    }
  }

  openOrders(): void {
    if (!this.ensureSaved()) {
      return;
    }

    this.router.navigateToChild(['/job-admin/job-orders', this.state.project(queryJeJobId)], {
      parentTitle: 'lbl_job_estimate',
    });
  }

  reprice(): void {
    const flow = this.repriceFlow();

    flow.run({
      form: this.form,
      value: this.form.submitValue,
    });
  }

  lockAll(): void {
    this.state.mutate({ type: JeDetailsStateEventType.LockAll });
  }

  unlockAll(): void {
    this.state.mutate({ type: JeDetailsStateEventType.UnlockAll });
  }

  authorize(): void {
    if (!this.ensureSaved()) {
      return;
    }

    this.dialogService.confirmation(yesNoConfirmation('msg_je_to_authorize')).pipe(
      filter$(yesAnswer),
      switchMap$(() =>
        // tslint:disable-next-line:no-non-null-assertion
        this.business.authorize(this.state.project(queryJeJobId), this.state.project(queryVersion)!).pipe(
          this.waitingService.operator())))
      .subscribe(
        ({ version }) => this.state.mutate({ type: JeDetailsStateEventType.Authorize, version }),
        this.notificationService.processAll);
  }

  unauthorize(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_je_to_unauthorize')).pipe(
      filter$(yesAnswer),
      switchMap$(() =>
        // tslint:disable-next-line:no-non-null-assertion
        this.business.unauthorize(this.state.project(queryJeJobId), this.state.project(queryVersion)!).pipe(
          this.waitingService.operator())))
      .subscribe(
        ({ version }) => this.state.mutate({ type: JeDetailsStateEventType.Unauthorize, version }),
        this.notificationService.processAll);
  }

  openGto(): void {
    if (this.state.project(queryJeEditable)) {
      // In Edit mode, GTO is opened only after all current changes were saved.
      if (this.state.project(queryDirty)) {
        this.dialogService.confirmation(yesNoConfirmation('msg_continue_will_save_changes')).pipe(
          filter$(yesAnswer),
          switchMap$(() => {
            const flow = this.completeFlow();

            return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form })
              .toFinalState().pipe(
                // we have to guarantee that GTO is opened only after successful save
                filter$((s) => !!s.success));
          })).subscribe(() => this.showGto());
      } else {
        this.showGto();
      }
    } else {
      if (this.state.project(queryJeAuthorized)) {
        this.notificationService.warning('msg_je_job_authorized');
        return;
      }
      // in View mode, we allow to open GTO only if there is at least one measured ToP
      if (this.state.project(queryJeHasTopsFromGto)) {
        this.showGto();
      } else {
        this.notificationService.warning('msg_no_gto_measurements');
      }
    }
  }

  openReport(reportUrl: string): void {
    if (!this.ensureSaved()) {
      return;
    }

    openInNewWindow(reportUrl);
  }

  componentConfirmVariation(componentId: ID): void {
    this.componentSetField(componentId, 'variationStatus', JeVariationStatus.Confirmed);
  }

  itemConfirmVariation(itemId: ID): void {
    this.costCenterItemSetField(itemId, 'variationStatus', JeVariationStatus.Confirmed);
  }

  itemChangeCc(itemId: ID): void {
    if (!this.validate()) {
      return;
    }

    const item = this.state.project(queryJeItem, itemId);
    if (item) {
      this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
        title: this.translations.get('lbl_select_cost_center'),
        applyButtonLabel: 'lbl_select',
        multiselection: false,
        preselected: [item.costCenterId],
        dataSourceFactory: ({ name }) =>
          memoryBasedSlicedDataSource(this.state.project(queryJeCostCentersByName, name)
            .map(jeCostCenterToFinderListItem)),
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(getSingleNewSelectedEntity))
        .subscribe((cc: CostCenterFinderListItem) => {
          this.state.mutate({
            type: JeDetailsStateEventType.CostCenterItemChangeCc,
            itemId,
            costCenterId: cc.id,
          });
          this.addToHistory();
      });
    }
  }

  orderAdd(costCenterId: ID): void {
    const cc = this.state.project(queryJeCostCenter, costCenterId);

    if (cc) {
      this.dialogService.openWithInput(jobOrderDetailsDialogRoute, <JobOrderDetailsDialogConfig>{
        allowSupplierChange: this.state.project(queryJeAllowSupplierChange),
        masterInfo: this.state.project(queryJeOrderDetailsMasterInfo),
        order: {
          costCenter: { id: cc.costCenterId, name: toString(cc.code) },
          orderNumber: this.state.project(queryJeNextOrderNumber, costCenterId),
        },
        preferredSuppliers: cc.suppliers || [],
        findAllOrderAttachments: this.findAllOrderAttachments.bind(this),
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(jeOrderFromOrderDetails))
        .subscribe((order) => {
          this.state.mutate({ type: JeDetailsStateEventType.OrderAdd, order });
          this.addToHistory();
        });
    }
  }

  orderOpen(orderId: ID): void {
    const order = this.state.project(queryJeOrder, orderId);
    const cc = order && this.state.project(queryJeCostCenter, order.costCenterId);

    if (order && cc) {
      this.dialogService.openWithInput(jobOrderDetailsDialogRoute, <JobOrderDetailsDialogConfig>{
        allowSupplierChange: this.state.project(queryJeAllowSupplierChange),
        masterInfo: this.state.project(queryJeOrderDetailsMasterInfo),
        order: jeOrderToOrderDetails(order, toString(cc.code)),
        preferredSuppliers: cc.suppliers || [],
        findAllOrderAttachments: this.findAllOrderAttachments.bind(this),
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(jeOrderFromOrderDetails))
        .subscribe((updatedOrder) => {
          this.state.mutate({ type: JeDetailsStateEventType.OrderUpdate, order: updatedOrder });
        });
    }
  }

  orderRemove(orderId: ID): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_je_order_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.state.mutate({ type: JeDetailsStateEventType.OrderRemove, orderId });
        this.addToHistory();
      },
    );
  }

  openStr(): void {
    this.dialogService.openWithInput(salesTaxRateFinderDialogRoute, <SalesTaxRateFinderDialogConfig>{
        multiselection: true,
        selectionModel: newSelectionModel(
          (this.state.project(queryJeSalesTaxRates)).map(jeStrToFinder),
          getSalesTaxRateListItemId),
        unselectable: true,
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllSelectedEntities),
      map$((rates) => rates.map(jeStrFromFinder)))
      .subscribe((rates) => this.state.mutate({ type: JeDetailsStateEventType.SalesTaxRatesSet, rates }));
  }

  layoutUpdate(layout: GridLayout): void {
    this.state.mutate({ type: JeDetailsStateEventType.LayoutUpdate, layout });
  }

  openAddOrdersDialog(orderList: JeOrderForSchedule[], details: JeDetailsProcess): Promise<JeDetailsProcess> {
    return new Promise((resolve) => {
      this.dialogService.openWithInput(jeAddOrdersDialogRoute, <JeAddOrders>{
        jobOrdersForSchedule: orderList,
        parentTitle: 'lbl_job',
      }).afterClosed().pipe(
        map$((answer: boolean) => {
          if (!answer) {
            resolve(details);
          }
          return answer;
        }),
        filter$(Boolean))
          .subscribe((jobOrders: JeOrderForSchedule[]) => {
            if (!this.context.hasPermission(BuilderPermission.Schedules.Construction.EDIT)
              && !this.context.hasPermission(BuilderPermission.Schedules.Construction.CREATE)) {
              this.notificationService.error('msg_denied_no_permissions');
              resolve(details);
              return;
            }
            if (jobOrders && jobOrders.length) {
              this.business.estimateOrders(this.state.project(queryJeJobId), jobOrders).subscribe(
                () => {
                  resolve(details);
                },
                (error) => {
                  resolve(details);
                  this.notificationService.processAll(error);
                },
              );
            } else {
              resolve(details);
            }
          });
    });
  }

  private showGto(): void {
    const { state } = this;
    const masterInfo = state.project(queryJeMasterInfo);
    const allTops = state.project(queryJeTops);

    this.dialogService.openWithInput(
      graphicalTakeOffMainDialogRoute,
      <GraphicalTakeOffMainConfig>{
        id: state.project(queryJeJobId),
        entryPoint: 'JE',
        title: masterInfo.jobNumber.toString(),
        allTops,
        gFolderId: masterInfo.jobDocsJobPlansFolderId,
      },
      {
        panelClass: 'cdk-overlay-large',
      }).afterClosed().pipe(
      switchMap$(() => this.business.initWithCostCenter(
        state.project(queryJeJobId),
        state.project(queryJeIsCccSelected) ? void 0 : state.project(queryJeSelectedCostCenterId)).pipe(
        this.waitingService.operator())))
      .subscribe(
        (response) => {
          this.state.mutate({
            type: JeDetailsStateEventType.LoadWithCostCenter,
            details: response,
            costCenterId: response.initialCostCenterId,
            costCenter: response.initialCostCenter,
          });

          this.updateFormulaContext();
        },
        this.notificationService.processAll,
      );
  }

  private validate(): boolean {
    if (this.state.project(queryJeHasErrors)) {
      this.state.mutate({ type: JeDetailsStateEventType.NavigateToInvalid });

      this.notificationService.error('msg_he_incorrect_data_in_tables');
      return false;
    }
    return true;
  }

  private validateCurrentElement(): boolean {
    if (this.state.project(queryJeElementHasErrors)) {
      this.state.mutate({ type: JeDetailsStateEventType.HighlightInvalid });
      return false;
    }
    return true;
  }

  private ensureCcLoad(costCenterId?: ID): Observable<JeDetailsStateEvent> {
    return of$(costCenterId).pipe(
      filter$(Boolean),
      map$((id) => this.state.project(queryJeCostCenter, id)),
      filter$(Boolean),
      filter$(isJeCcNotPreloaded),
      switchMap$((cc) =>
        this.business.loadCostCenter(
          this.state.project(queryJeJobId),
          getJeCostCenterId(cc),
          this.state.project(queryJeDirtyData),
          this.createJeMappingContext()).pipe(
          (this.waitingService.operator<JeGetCostCenterResponse>()),
          switchMap$((complete) => {
            // tslint:disable-next-line:no-non-null-assertion
            if (complete.jobCostCenter.version! > cc.version!
              && this.state.project(queryJeCostCenterHasDirtyChanges, getJeCostCenterId(cc))) {
              return throwError$(MessageBasket.error('msg_optimistic_lock_exception'));
            } else {
              return [complete];
            }
          }))),
      map$((costCenter) => (<JeDetailsStateCostCenterLoadEvent>{
        type: JeDetailsStateEventType.CostCenterLoad,
        costCenterId,
        costCenter,
      })));
  }

  private createJeMappingContext(): JeMappingContext {
    return {
      getUom: (id: ID) => this.state.project(queryJeUomRefItem, id),
      getTop: (id: ID) => this.state.project(queryJeTopRefItem, id),
    };
  }

  private ensureSaved(): boolean {
    if (this.state.project(queryDirty)) {
      this.notificationService.error('msg_save_first');
      return false;
    }
    return true;
  }

  /**
   * Generates orders for all new cost centers
   */
  private generateOrdersForNewCostCenters(componentSet: JeGetComponentsResponse): Observable<JeGetComponentsResponse> {
    const { affectedCostCenters } = componentSet;
    const newCostCenterIds = affectedCostCenters
      .map(getJeCostCenterId)
      .filter((costCenterId) => !this.state.project(queryJeHasCostCenter, costCenterId));
    return isEmpty(newCostCenterIds)
      ? of$(componentSet)
      : this.business.generateOrders(newCostCenterIds).pipe(
        // group generated orders by cost centers
        map$((orders) => keyBy(orders, (order) => order.costCenterId)),
        // create each order from generated one
        map$((ordersByCostCenterId) => mapValues(
          ordersByCostCenterId,
          (order) => jeOrderFromGenerated(order, 0))),
        // merge all groups into single array of orders
        map$((ordersByCostCenterId) => flatten(values(ordersByCostCenterId))),
        // merge new orders into initial set of components
        map$((newOrders) => ({ ...componentSet, newOrders })));
  }

  /**
   * Merges first CC order into each item
   */
  private mergeFirstOrdersIntoItems(items: JeItem[]): JeItem[] {
    return items.map((item) => {
      const order = this.state.project(queryJeCostCenterOrder0, item.costCenterId);
      return order ? setEntityField(item, 'order', order) : item;
    });
  }

  /**
   * Merges first CC order into each component item
   */
  private mergeFirstOrdersIntoComponentItems(response: JeGetComponentsResponse): JeGetComponentsResponse {
    const { items, newOrders } = response;
    const newOrderIndex = keyBy(newOrders, (order) => order.costCenterId);
    return {
      ...response,
      items: items.map((item) => {
        // take either new generated order or existing one
        const order = newOrderIndex[item.costCenterId]
          || this.state.project(queryJeCostCenterOrder0, item.costCenterId);
        return order ? setEntityField(item, 'order', order) : item;
      }),
    };
  }

  private findAllOrderAttachments(ccCode: number, orderNumber: number): Observable<FileListItem[]> {
    const cc = this.state.project(queryJeCostCenterByCode, ccCode);
    const order = cc ? this.state.project(queryJeCostCenterOrderByNumber, cc.costCenterId, orderNumber) : void 0;

    return cc && order
      ? isEmpty(order.attachments)
        ? throwError$(MessageBasket.warning('msg_no_files_to_clone'))
        : of$(order.attachments.map(communicationAttachmentToFile))
      : throwError$(MessageBasket.error('msg_no_cc_order_to_clone_from'));
  }

  private testElementFormula(event: FormulaTestEvent, element: JeElement): void {
    if (element && element.formula) {
      const invalidTop = this.state.project(queryJeFormulaFirstInvalidTop, element.formula);
      if (invalidTop) {
        this.state.mutate({ type: JeDetailsStateEventType.NavigateToInvalidTop, topId: getJeTopId(invalidTop) });
        event.setError(this.translations.get('lbl_incorrect_top_value'));
      } else {
        const request = this.state.project(queryJeElementFormulaCalculation, element);
        if (request) {
          event.setValue(this.formulaCalculation.calculate(request, NumberTypes.SignedQuantity));
        }
      }
    }
  }

  private updateFormulaContext(): void {
    const { formulaContext } = getRouteInitData(this.route);

    const variables = this.state.project(queryJeTopRefItems).map(jeFormulaVariableFromTop);
    const activeVariables = variables.filter(isFormulaVariableActive);
    formulaContext.setVariables(keyBy(variables, 'id'));
    formulaContext.setSnippets(FormulaElementGroup.TakeOffParams, variablesToSnippets(activeVariables));
  }

  private ensureDependenciesExist(items: ComponentItemFinderListItem[]): Observable<void> {
    return combineLatest$(this.ensureUomExist(items), this.ensureTopExist(items)).pipe(mapTo$(void 0));
  }

  private ensureUomExist(items: ComponentItemFinderListItem[]): Observable<void> {
    const notExistingUomIds = uniq(items
      .map(({ uomId }) => uomId)
      .filter((uomId) => isNil(this.state.project(queryJeUomRefItem, uomId))));
    if (notExistingUomIds.length > 0) {
      return combineLatest$(notExistingUomIds.map((uomId) => this.business.getUom(uomId))).pipe(
        tap$((uomSet) => this.state.mutate({ type: JeDetailsStateEventType.UomAddSet, uomSet })),
        mapTo$(void 0));
    } else {
      return of$(void 0);
    }
  }

  private ensureTopExist(items: ComponentItemFinderListItem[]): Observable<void> {
    const notExistingTopIds = uniq(flatMap(items, ({ topIds }) => topIds || []))
      .filter((topId) => isNil(this.state.project(queryJeTopRefItem, topId)));
    if (notExistingTopIds.length > 0) {
      return combineLatest$(notExistingTopIds.map((topId) => this.business.getTop(topId))).pipe(
        tap$((topSet) => {
          this.state.mutate({ type: JeDetailsStateEventType.TopAddSet, topSet });
          this.updateFormulaContext();
        }),
        mapTo$(void 0));
    } else {
      return of$(void 0);
    }
  }
}
